import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Pagination, TextField} from "@mui/material";
import {Header,} from "./GroupList.styled";
import {ReactComponent as UsersPlusIcon} from "../../../assets/icons/users-plus.svg";
import {formatDate} from "../../../utils/format-date";
import {useNavigate} from "react-router-dom";
import {ReactComponent as TrashIcon} from "../../../assets/icons/trash.svg";
import {ReactComponent as ArrowCircleRight} from "../../../assets/icons/arrow-circle-right.svg";
import ConfirmationDialog from "../../../components/dialogs/ConfirmationDialog";
import {ActionButton, CancelButton} from "../../../components/common/ActionButtons";
import {parseError} from "../../../errors/http.service";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {useTranslation} from 'react-i18next';
import SearchInput from "../../../components/common/SearchInput";
import PageSizeSelector from "../../../components/common/PageSizeSelector";
import ResponsiveTable, {ResponsiveTableColumn} from "../../../components/common/ResponsiveTable";
import {
    ControlsRow,
    PageSizeWrapper,
    PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../../components/common/TableContainer.styled";
import FieldHelpSuffix from "../../../components/common/FieldHelpSuffix";
import {ValidationError} from "../../../utils/validation";
import {validateRequiredTrimmed} from "../../../utils/required-validation";
import {groupsService} from "../../../services/groups.service";
import {Group} from "../../../models/groups.models";

const SEARCH_DEBOUNCE_MS = 400;

const GroupList = () => {
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const pageSizeOptions = [5, 10, 20, 50];

    const [groups, setGroups] = useState<Group[]>([]);
    const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false);

    const [groupName, setGroupName] = useState("");
    const [groupNameTouched, setGroupNameTouched] = useState(false);

    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");

    const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
    const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);

    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

    const groupNameValidationError: ValidationError | null = validateRequiredTrimmed(groupName, 'groupList.createDialog.error');
    const showValidationError = (groupNameTouched || submitted) && !!groupNameValidationError;

    useEffect(() => {
        const normalizedSearchValue = searchValue.trim();
        const normalizedDebouncedSearchValue = debouncedSearchValue.trim();

        if (normalizedSearchValue === normalizedDebouncedSearchValue) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [debouncedSearchValue, searchValue]);

    const fetchData = useCallback(async () => {
        try {
            const fetchedGroups = await groupsService.fetchGroups(currentPage - 1, pageSize, debouncedSearchValue);
            setGroups(fetchedGroups.content);
            setTotalElements(fetchedGroups.totalElements);
        } catch (error) {
            showSnackbar(parseError(error), "error");
            setGroups([]);
            setTotalElements(0);
        }
    }, [currentPage, debouncedSearchValue, pageSize, showSnackbar]);

    useEffect(() => {
        void fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!isCreateGroupDialogOpen) {
            setGroupName("");
            setGroupNameTouched(false);
            setSubmitted(false);
            setServerError("");
        }
    }, [isCreateGroupDialogOpen]);

    const handleCreateGroup = async () => {
        setSubmitted(true);
        setServerError("");

        if (groupNameValidationError) {
            return;
        }
        try {
            await groupsService.createGroup(groupName);

            setIsCreateGroupDialogOpen(false);
            setGroupName("");
            setGroupNameTouched(false);
            setServerError("");

            await fetchData();
        } catch (error) {
            setServerError(parseError(error));
        } finally {
            setSubmitted(false);
        }
    };

    const navigateToGroupDetails = useCallback((id: number, name: string) => {
        navigate(`/admin/group/${id}?name=${name}`)
    }, [navigate]);

    const openDeleteDialog = useCallback((group: Group) => {
        setGroupToDelete(group);
        setIsDeleteConfirmDialogOpen(true);
    }, []);

    const confirmRemoveGroup = async () => {
        if (!groupToDelete) return;
        try {
            await groupsService.removeGroupById(groupToDelete.id);
            await fetchData();
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            setIsDeleteConfirmDialogOpen(false);
            setGroupToDelete(null);
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
    };

    const columns: ResponsiveTableColumn<Group>[] = useMemo(() => [
        {
            id: 'name',
            header: t('groupList.tableHeaders.name'),
            render: (g) => g.name,
        },
        {
            id: 'createdAt',
            header: t('groupList.tableHeaders.createdAt'),
            render: (g) => formatDate(g.createdAt),
        },
        {
            id: 'actions',
            header: t('groupList.tableHeaders.actions'),
            cellClassName: 'actions-cell',
            render: ((g) => !g.isDefault
                ? (
                    <Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => openDeleteDialog(g)}
                            aria-label={t('groupList.deleteButton') as string}
                        >
                            <TrashIcon aria-hidden={true} focusable={false}/>
                            <span style={{marginLeft: '8px'}}>{t('groupList.deleteButton')}</span>
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigateToGroupDetails(g.id, g.name)}
                            aria-label={t('groupList.viewDetailsButton') as string}
                        >
                            <ArrowCircleRight aria-hidden={true} focusable={false}/>
                            <span style={{marginLeft: '8px'}}>{t('groupList.viewDetailsButton')}</span>
                        </Button>
                    </Box>
                )
                : ''
            )
        },
    ], [t, openDeleteDialog, navigateToGroupDetails]);

    return (
        <StyledTableContainer role="region" aria-labelledby="group-list-title">
                <Header>
                    <h2 id="group-list-title">{t('groupList.title')}</h2>
                    <Button
                        startIcon={<UsersPlusIcon aria-hidden={true} focusable={false}/>} 
                        variant="contained" color="primary" onClick={() => setIsCreateGroupDialogOpen(true)}
                        aria-label={t('groupList.createButton') as string}
                    >
                        {t('groupList.createButton')}
                    </Button>
                </Header>

                <ControlsRow>
                    <SearchWrapper>
                        <SearchInput onChange={handleSearchChange} value={searchValue} ariaLabel={t('groupList.search.ariaLabel') as string}/>
                    </SearchWrapper>
                    <PageSizeWrapper>
                        <PageSizeSelector
                            options={pageSizeOptions}
                            selectedSize={pageSize}
                            onChange={handlePageSizeChange}
                            ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                            getSizeAriaLabel={(size) => t('pageSizeSelector.aria.setSize', { size }) as string}
                        />
                    </PageSizeWrapper>
                </ControlsRow>

                <ResponsiveTable
                    columns={columns}
                    data={groups}
                    getRowKey={(g) => g.id}
                    emptyState={t('groupList.emptyState')}
                    ariaLabel={t('groupList.table.ariaLabel') as string}
                />
                {totalElements > pageSize && (
                    <PaginationWrapper>
                        <Pagination
                            count={Math.ceil(totalElements / pageSize)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            aria-label={t('groupList.pagination.ariaLabel') as string}
                        />
                    </PaginationWrapper>
                )}
            <Dialog
                open={isCreateGroupDialogOpen}
                onClose={() => setIsCreateGroupDialogOpen(false)}
                fullWidth
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 'auto' },
                        m: { xs: 1.5, sm: '24px' },
                    }
                }}
            >
                <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 0 }}>
                    {t('groupList.createDialog.title')}
                </DialogTitle>

                <DialogContent sx={{ padding: { xs: '16px 20px !important', sm: '32px 64px !important' }, width: { xs: '100%', sm: '400px' } }}>
                    <TextField
                        label={t('groupList.createDialog.groupNameLabel')}
                        fullWidth
                        value={groupName}
                        onChange={(e) => {
                            setGroupName(e.target.value);
                            setServerError("");
                        }}
                        onBlur={() => setGroupNameTouched(true)}
                        error={showValidationError || !!serverError}
                        helperText={showValidationError ? String(t(groupNameValidationError!.key)) : (serverError || ' ')}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('groupList.createDialog.help.groupName')}
                                    ariaLabel={t('groupList.createDialog.help.groupName') as string}
                                />
                            ),
                        }}
                        sx={{ mt: { xs: 1, sm: 0 } }}
                    />
                </DialogContent>
                <DialogActions
                    sx={{
                        paddingBottom: { xs: '16px', sm: '20px' },
                        paddingRight: { xs: '16px', sm: '20px' },
                        paddingLeft: { xs: '16px', sm: '20px' },
                        justifyContent: { xs: 'stretch', sm: 'space-between' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 },
                    }}
                >
                    <CancelButton
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                        onClick={() => {
                            setIsCreateGroupDialogOpen(false)
                        }}
                    >
                        {t('groupList.cancelButton')}
                    </CancelButton>
                    <ActionButton sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={handleCreateGroup}>
                        {t('groupList.createButton')}
                    </ActionButton>
                </DialogActions>
            </Dialog>

            {groupToDelete &&  (
                <ConfirmationDialog
                    open={isDeleteConfirmDialogOpen}
                    onClose={() => setIsDeleteConfirmDialogOpen(false)}
                    onConfirm={confirmRemoveGroup}
                    title={t('groupList.deleteDialog.title')}
                    message={<>Czy na pewno chcesz usunąć grupę: &ldquo;{groupToDelete.name}&rdquo;?</>}
                    confirmButtonText={t('common.delete') as string}
                    cancelButtonText={t('common.cancel') as string}
                />
            )}
        </StyledTableContainer>
    );
};
// TODO: to fix translations in ConfirmationDialog

export default GroupList;
